export const CATALOG_COVER_ASPECT_RATIO = 16 / 9
export const CATALOG_COVER_RATIO_LABEL = '16:9'
export const CATALOG_COVER_RECOMMENDED_SIZE_LABEL = '1920×1080 px'
export const CATALOG_COVER_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const

const CATALOG_COVER_RATIO_TOLERANCE = 0.02

export type ImageDimensions = {
  width: number
  height: number
}

export function isCatalogCoverMimeTypeAllowed(type: string) {
  return CATALOG_COVER_ALLOWED_TYPES.includes(
    type.toLowerCase() as (typeof CATALOG_COVER_ALLOWED_TYPES)[number]
  )
}

export function isCatalogCoverAspectRatioAllowed({ width, height }: ImageDimensions) {
  if (width <= 0 || height <= 0) {
    return false
  }

  return Math.abs(width / height - CATALOG_COVER_ASPECT_RATIO) <= CATALOG_COVER_RATIO_TOLERANCE
}

export function formatCatalogCoverValidationHint() {
  return `Обложка должна быть ${CATALOG_COVER_RATIO_LABEL}, рекомендуемый размер ${CATALOG_COVER_RECOMMENDED_SIZE_LABEL}.`
}

export function formatCatalogCoverAspectRatioError({ width, height }: ImageDimensions) {
  return `${formatCatalogCoverValidationHint()} Текущий размер: ${width}×${height}. Обрежьте изображение перед загрузкой.`
}

async function getImageDimensions(file: File): Promise<ImageDimensions> {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file)

    try {
      return {
        width: bitmap.width,
        height: bitmap.height,
      }
    } finally {
      bitmap.close()
    }
  }

  return new Promise((resolve, reject) => {
    const image = new Image()
    const objectUrl = URL.createObjectURL(file)

    image.onload = () => {
      URL.revokeObjectURL(objectUrl)
      resolve({
        width: image.naturalWidth,
        height: image.naturalHeight,
      })
    }
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('Не удалось прочитать размер изображения.'))
    }
    image.src = objectUrl
  })
}

export async function validateCatalogCoverImage(file: File) {
  if (!isCatalogCoverMimeTypeAllowed(file.type || '')) {
    throw new Error('Обложка должна быть JPG, PNG или WebP.')
  }

  const dimensions = await getImageDimensions(file)

  if (!isCatalogCoverAspectRatioAllowed(dimensions)) {
    throw new Error(formatCatalogCoverAspectRatioError(dimensions))
  }
}
